class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-cart-discount-form]');
    this.input = this.querySelector('input[name="discount"]');
    this.errorEl = this.querySelector('[data-cart-discount-error]');
    this.submitBtn = this.form && this.form.querySelector('[type="submit"]');

    if (!this._bound) {
      this._bound = true;
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-discount-remove]');
        if (button && this.contains(button)) this.onRemove(event);
      });
    }

    queueMicrotask(() => {
      if (!this._synced) this.syncFromCart();
    });
  }

  getSectionsPayload() {
    const sections = [];
    const cartFooter = document.getElementById('main-cart-footer');

    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
    if (cartFooter && cartFooter.dataset.id) sections.push(cartFooter.dataset.id);

    return {
      sections,
      sections_url: window.location.pathname,
    };
  }

  setLoading(isLoading) {
    if (this.submitBtn) this.submitBtn.disabled = isLoading;
    if (this.input) this.input.disabled = isLoading;
    this.querySelectorAll('[data-discount-remove]').forEach((button) => {
      button.disabled = isLoading;
    });
    this.classList.toggle('cart-discount--loading', isLoading);
  }

  setError(message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    } else {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  codeIsApplied(cart, code) {
    if (!cart || !code) return false;
    const normalized = code.trim().toUpperCase();

    const fromApplications = (cart.cart_level_discount_applications || []).some((discount) => {
      return String(discount.title || '').toUpperCase() === normalized;
    });
    if (fromApplications) return true;

    const fromCodes = (cart.discount_codes || []).some((entry) => {
      return String(entry.code || '').toUpperCase() === normalized && entry.applicable !== false;
    });
    if (fromCodes) return true;

    return (cart.items || []).some((item) => {
      const lineTitles = (item.discounts || []).map((discount) => String(discount.title || '').toUpperCase());
      const allocationTitles = (item.line_level_discount_allocations || []).map((allocation) =>
        String((allocation.discount_application && allocation.discount_application.title) || '').toUpperCase()
      );
      return lineTitles.concat(allocationTitles).includes(normalized);
    });
  }

  async fetchCartJson() {
    const cartUrl = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/$/, '') + '.js';
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('cart_fetch_failed');
    return response.json();
  }

  async fetchSectionsHtml() {
    const { sections, sections_url } = this.getSectionsPayload();
    if (!sections.length) return {};

    const base = sections_url || '/';
    const url = `${base}${base.includes('?') ? '&' : '?'}sections=${encodeURIComponent(sections.join(','))}`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return {};
    return response.json();
  }

  async updateDiscount(discountValue) {
    const response = await fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: discountValue }),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(
        data.description ||
          data.message ||
          (window.cartStrings && window.cartStrings.discountError) ||
          'Could not update discount'
      );
    }
    return data;
  }

  async applyViaDiscountRoute(code) {
    await fetch(`/discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });

    const [cart, sections] = await Promise.all([this.fetchCartJson(), this.fetchSectionsHtml()]);
    return { ...cart, sections };
  }

  replaceSections(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    if (parsedState.sections['cart-drawer']) {
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      if (drawerInner) {
        drawerInner.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-drawer'], '.drawer__inner');
      }
    }

    if (parsedState.sections['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        icon.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
      }
    }

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && cartFooter.dataset.id && parsedState.sections[cartFooter.dataset.id]) {
      const contents = cartFooter.querySelector('.js-contents');
      if (contents) {
        contents.innerHTML = this.getSectionInnerHTML(parsedState.sections[cartFooter.dataset.id], '.js-contents');
      }
    }

    document.querySelectorAll('cart-discount').forEach((el) => {
      if (typeof el.syncFromCart === 'function') el.syncFromCart(parsedState);
    });
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  formatMoney(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, window.shopMoneyFormat);
    }

    const format = window.shopMoneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return String(value / 100);

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      const safePrecision = precision == null ? 2 : precision;
      const thousandSep = thousands == null ? ',' : thousands;
      const decimalSep = decimal == null ? '.' : decimal;
      const fixed = (Number(number) / 100).toFixed(safePrecision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousandSep);
      return parts[1] ? parts[0] + decimalSep + parts[1] : parts[0];
    };

    let formatted = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }

    return format.replace(placeholder, formatted);
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  collectDiscountTitles(cart) {
    const titles = [];
    const seen = new Set();
    const add = (title) => {
      const text = String(title || '').trim();
      if (!text) return;
      const key = text.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      titles.push(text);
    };

    (cart.discount_codes || []).forEach((entry) => {
      if (entry && entry.applicable !== false) add(entry.code);
    });
    (cart.cart_level_discount_applications || []).forEach((discount) => add(discount.title));
    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => add(discount.title));
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        add(allocation.discount_application && allocation.discount_application.title);
      });
    });

    return titles;
  }

  removeLabel(code) {
    const template =
      (window.cartStrings && window.cartStrings.removeDiscount) || 'Remove discount [code]';
    return template.replace('[code]', code);
  }

  renderDiscountTags(titles) {
    const list = this.querySelector('[data-cart-discount-tags]');
    if (!list) return;

    const template = this.querySelector('[data-cart-discount-tag-template]');
    list.innerHTML = '';

    titles.forEach((title) => {
      let item;
      if (template && template.content) {
        item = template.content.firstElementChild.cloneNode(true);
      } else {
        item = document.createElement('li');
        item.className = 'cart-discount__tag';
        item.innerHTML =
          '<span class="cart-discount__tag-title"></span><button type="button" class="cart-discount__tag-remove" data-discount-remove></button>';
      }

      const titleEl = item.querySelector('.cart-discount__tag-title');
      if (titleEl) titleEl.textContent = title;

      const button = item.querySelector('[data-discount-remove]');
      if (button) {
        button.setAttribute('data-discount-code', title);
        button.setAttribute('aria-label', this.removeLabel(title));
      }

      list.appendChild(item);
    });

    list.hidden = titles.length === 0;
  }

  renderTotals(cart) {
    const totalDiscount = Number(cart.total_discount || 0);
    const totalPrice = Number(cart.total_price || 0);
    const originalTotal = Number(cart.original_total_price || 0);
    const itemsSubtotal = Number(cart.items_subtotal_price || 0);
    const displaySubtotal = totalDiscount > 0 ? originalTotal || itemsSubtotal : itemsSubtotal;

    const subtotalEl = this.querySelector('[data-cart-discount-subtotal]');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(displaySubtotal);

    const savingsRow = this.querySelector('[data-cart-discount-savings]');
    const savingsAmount = this.querySelector('[data-cart-discount-savings-amount]');
    if (savingsRow) {
      if (totalDiscount > 0) {
        if (savingsAmount) savingsAmount.textContent = '−' + this.formatMoney(totalDiscount);
        savingsRow.hidden = false;
      } else {
        if (savingsAmount) savingsAmount.textContent = '';
        savingsRow.hidden = true;
      }
    }

    const totalEl = this.querySelector('[data-cart-discount-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(totalPrice);

    this.dataset.cartTotal = String(totalPrice);
  }

  replacePriceNode(current, html) {
    if (!current) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const next = wrapper.firstElementChild;
    if (next) current.replaceWith(next);
  }

  syncLinePriceDisplays(row, item) {
    const originalUnit = Number(item.original_price != null ? item.original_price : item.price);
    const finalUnit = Number(item.final_price != null ? item.final_price : item.discounted_price);
    const originalLine = Number(item.original_line_price);
    const finalLine = Number(item.final_line_price != null ? item.final_line_price : item.line_price);

    const details = row.querySelector('.cart-item__details');
    if (details) {
      const unitPrice = details.querySelector(':scope > .cart-item__discounted-prices, :scope > .product-option');
      if (unitPrice) {
        const html =
          originalUnit !== finalUnit
            ? `<div class="cart-item__discounted-prices"><s class="cart-item__old-price product-option">${this.escapeHtml(
                this.formatMoney(originalUnit)
              )}</s><strong class="cart-item__final-price product-option">${this.escapeHtml(
                this.formatMoney(finalUnit)
              )}</strong></div>`
            : `<div class="product-option">${this.escapeHtml(this.formatMoney(originalUnit))}</div>`;
        this.replacePriceNode(unitPrice, html);
      }
    }

    row.querySelectorAll('.cart-item__price-wrapper').forEach((wrapper) => {
      const linePrice = wrapper.querySelector('.cart-item__discounted-prices, span.price.price--end');
      if (!linePrice || linePrice.closest('.unit-price')) return;
      const html =
        originalLine !== finalLine
          ? `<div class="cart-item__discounted-prices"><s class="cart-item__old-price price price--end">${this.escapeHtml(
              this.formatMoney(originalLine)
            )}</s><span class="price price--end">${this.escapeHtml(this.formatMoney(finalLine))}</span></div>`
          : `<span class="price price--end">${this.escapeHtml(this.formatMoney(originalLine))}</span>`;
      this.replacePriceNode(linePrice, html);
    });
  }

  syncLineDiscountList(row, item) {
    const list = row.querySelector('ul.discounts');
    if (!list) return;

    const titles = [];
    const seen = new Set();
    const add = (title) => {
      const text = String(title || '').trim();
      if (!text) return;
      const key = text.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      titles.push(text);
    };

    (item.discounts || []).forEach((discount) => add(discount.title));
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      add(allocation.discount_application && allocation.discount_application.title);
    });

    const tagTemplate = this.querySelector('[data-cart-discount-tag-template]');
    const iconTemplate = tagTemplate && tagTemplate.content.querySelector('.icon-discount');
    const iconHtml = iconTemplate ? iconTemplate.outerHTML : '';
    list.innerHTML = titles
      .map((title) => `<li class="discounts__discount">${iconHtml}${this.escapeHtml(title)}</li>`)
      .join('');
  }

  syncLinePrices(cart) {
    const items = cart.items || [];
    const root = this.closest('cart-drawer') || document.getElementById('main-cart-items');
    if (!root) return;

    const rows = root.querySelectorAll('tr.cart-item');
    items.forEach((item, index) => {
      const row = rows[index];
      if (!row) return;
      this.syncLinePriceDisplays(row, item);
      this.syncLineDiscountList(row, item);
    });
  }

  isCartPayload(cart) {
    return Boolean(cart && typeof cart.item_count === 'number' && Array.isArray(cart.items));
  }

  async syncFromCart(cart) {
    this._synced = true;

    try {
      const data = this.isCartPayload(cart) ? cart : await this.fetchCartJson();
      this.renderDiscountTags(this.collectDiscountTitles(data));
      this.renderTotals(data);
      this.syncLinePrices(data);
    } catch (error) {
      console.error('cart-discount: failed to sync applied discounts', error);
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    this.setError('');

    const code = (this.input && this.input.value ? this.input.value : '').trim();
    if (!code) {
      this.setError((window.cartStrings && window.cartStrings.discountEmpty) || 'Enter a discount code');
      return;
    }

    this.setLoading(true);

    try {
      let parsedState;

      try {
        parsedState = await this.updateDiscount(code);
      } catch (updateError) {
        parsedState = null;
      }

      if (!parsedState || !this.codeIsApplied(parsedState, code)) {
        parsedState = await this.applyViaDiscountRoute(code);
      }

      if (!parsedState.sections || !Object.keys(parsedState.sections).length) {
        parsedState.sections = await this.fetchSectionsHtml();
      }

      const previousTotal = Number(this.dataset.cartTotal || 0);
      const applied =
        this.codeIsApplied(parsedState, code) ||
        (previousTotal > 0 && Number(parsedState.total_price) < previousTotal);

      if (!applied) {
        throw new Error(
          (window.cartStrings && window.cartStrings.discountInvalid) || 'That discount code could not be applied'
        );
      }

      this.replaceSections(parsedState);
    } catch (error) {
      this.setError(
        (error && error.message) ||
          (window.cartStrings && window.cartStrings.discountError) ||
          'Could not apply discount code'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async onRemove(event) {
    event.preventDefault();
    this.setError('');
    this.setLoading(true);

    try {
      document.cookie = 'discount_code=; Path=/; Max-Age=0';

      let parsedState = await this.updateDiscount('');

      if (!parsedState.sections || !Object.keys(parsedState.sections).length) {
        parsedState.sections = await this.fetchSectionsHtml();
      }

      this.replaceSections(parsedState);
    } catch (error) {
      this.setError(
        (error && error.message) ||
          (window.cartStrings && window.cartStrings.discountError) ||
          'Could not remove discount code'
      );
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
